/**
 * Compute concrete due-date occurrences for ScheduledItem rows. Replaces
 * the proration-by-window approach used previously — a monthly bill due
 * on the 1st now lands EXACTLY on the 1st in the running balance and in
 * the cash-position breakdown, instead of accruing fractionally each day.
 *
 * Pure ISO-date math (no Date allocations beyond the iterator). Items
 * with `active === false` yield nothing.
 */

import type {
  AppData,
  ScheduledItem,
  ScheduledFrequency,
  Transaction,
} from '../data/types';
import { newId } from './id';
import { getPayPeriod } from './payPeriod';
import { startOfDay, toIsoDate } from './date';

const isoToDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map((n) => Number(n));
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
};

const dateToIso = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDaysIso = (iso: string, n: number): string => {
  const d = isoToDate(iso);
  d.setDate(d.getDate() + n);
  return dateToIso(d);
};

const addMonthsIso = (iso: string, n: number): string => {
  const d = isoToDate(iso);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  // Clamp the day to the new month's length so Jan 31 + 1 month → Feb 28
  // (not March 3 via the JS overflow rule).
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return dateToIso(d);
};

const addYearsIso = (iso: string, n: number): string => addMonthsIso(iso, n * 12);

function stepIso(iso: string, freq: ScheduledFrequency, dir: 1 | -1): string {
  switch (freq) {
    case 'weekly':
      return addDaysIso(iso, 7 * dir);
    case 'biweekly':
      return addDaysIso(iso, 14 * dir);
    case 'monthly':
      return addMonthsIso(iso, 1 * dir);
    case 'yearly':
      return addYearsIso(iso, 1 * dir);
    case 'one_time':
      return iso;
  }
}

/**
 * Yields every due-date ISO string for `item` that falls within
 * [fromIso, toIso] inclusive. For one_time, yields nextDate when in
 * range. For recurring, anchors on nextDate and walks back/forward by
 * the frequency interval.
 */
export function* scheduledOccurrencesInRange(
  item: ScheduledItem,
  fromIso: string,
  toIso: string,
): Generator<string> {
  if (!item.active) return;
  if (toIso < fromIso) return;
  if (item.frequency === 'one_time') {
    if (item.nextDate >= fromIso && item.nextDate <= toIso) {
      yield item.nextDate;
    }
    return;
  }
  // Anchor on nextDate; back-step until just before fromIso, then forward.
  let cur = item.nextDate;
  while (cur > fromIso) {
    const prev = stepIso(cur, item.frequency, -1);
    if (prev < fromIso) break;
    cur = prev;
  }
  // If still before window, step forward into it.
  while (cur < fromIso) {
    cur = stepIso(cur, item.frequency, 1);
  }
  while (cur <= toIso) {
    yield cur;
    cur = stepIso(cur, item.frequency, 1);
  }
}

/**
 * Sum amounts of every expense scheduled-item occurrence that lands in
 * [fromIso, toIso] inclusive.
 */
export function sumScheduledBillsInRange(
  items: ScheduledItem[],
  fromIso: string,
  toIso: string,
): number {
  let sum = 0;
  for (const item of items) {
    if (item.type !== 'expense') continue;
    for (const _ of scheduledOccurrencesInRange(item, fromIso, toIso)) {
      sum += item.amount;
    }
  }
  return sum;
}

/**
 * Materialize due scheduled bills as real transactions. For every active
 * expense ScheduledItem, walk the occurrences in [periodStart, today]
 * and create a Transaction tagged with `generatedFromScheduledId` for
 * each due date that doesn't already have one. After generation, advance
 * the item's `nextDate` past today so the Plan tab shows the next
 * upcoming occurrence.
 *
 * Idempotent — running twice on the same data is a no-op because the
 * dedupe set is keyed on (scheduledItemId, dueDate). Returns the
 * original AppData reference unchanged when nothing was materialized,
 * so callers can use reference equality to skip a persist.
 */
export function processScheduledBills(data: AppData): AppData {
  const today = startOfDay(new Date());
  const todayIso = toIsoDate(today);
  const period = getPayPeriod(data.profile, today);
  const fromIso = toIsoDate(period.start);
  if (todayIso < fromIso) return data;

  // Pre-index existing generated transactions by scheduled-item id +
  // date so dedup is O(1) per occurrence instead of a per-occurrence
  // array scan.
  const generatedByItem = new Map<string, Set<string>>();
  for (const t of data.transactions) {
    const sid = t.generatedFromScheduledId;
    if (!sid) continue;
    let dates = generatedByItem.get(sid);
    if (!dates) {
      dates = new Set<string>();
      generatedByItem.set(sid, dates);
    }
    dates.add(t.date);
  }

  const newTransactions: Transaction[] = [];
  let itemsChanged = false;
  const nowIso = new Date().toISOString();

  const updatedItems = data.scheduledItems.map((item) => {
    if (!item.active || item.type !== 'expense') return item;

    const seen = generatedByItem.get(item.id);
    for (const dueDate of scheduledOccurrencesInRange(item, fromIso, todayIso)) {
      if (seen?.has(dueDate)) continue;
      newTransactions.push({
        id: newId(),
        amount: item.amount,
        type: 'expense',
        categoryId: item.categoryId,
        description: item.name,
        date: dueDate,
        createdAt: nowIso,
        source: 'manual',
        recurring: true,
        generatedFromScheduledId: item.id,
      });
    }

    if (item.frequency === 'one_time') return item;
    if (item.nextDate > todayIso) return item;
    let next = item.nextDate;
    while (next <= todayIso) {
      next = stepIso(next, item.frequency, 1);
    }
    if (next === item.nextDate) return item;
    itemsChanged = true;
    return { ...item, nextDate: next };
  });

  if (newTransactions.length === 0 && !itemsChanged) return data;

  return {
    ...data,
    transactions:
      newTransactions.length > 0
        ? [...data.transactions, ...newTransactions]
        : data.transactions,
    scheduledItems: itemsChanged ? updatedItems : data.scheduledItems,
  };
}
